"use client";

import {
  startTransition,
  useEffect,
  useEffectEvent,
  useId,
  useImperativeHandle,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type Ref,
} from "react";
import { searchPlaces } from "@/lib/api/misc";
import type { PlaceResult } from "@/lib/api/misc";

export interface SavedCity {
  name: string;
  latitude: number;
  longitude: number;
  timezone?: string;
  country?: string;
}

interface TimingCitySelectorProps {
  city: SavedCity;
  onCitySelect: (city: SavedCity) => void;
  countryFilter?: string;
  /** Search-box copy language; the city name itself stays as selected. */
  locale?: "en" | "hi";
  /** Receives the raw place (with its GeoNames id) instead of `onCitySelect`. */
  onPlaceSelect?: (place: PlaceResult) => void;
  /**
   * "popover" (default): a city button that opens a search box.
   * "field": the search box itself, prefilled with the city, for a form that
   * wraps it in its own <label> (the timing hubs' city control, xb-02).
   */
  variant?: "popover" | "field";
  /** Classes for the "field" variant's input (it sits in the form's own row). */
  fieldClassName?: string;
  /** "field" variant: lets the surrounding form resolve a typed, unpicked town. */
  fieldRef?: Ref<TimingCityFieldHandle>;
}

export interface TimingCityFieldHandle {
  /**
   * Called from the form's submit. When the field holds a town the seeker typed
   * but never picked, it searches that text now, opens the first match and
   * returns true (the caller must cancel the submit, which would otherwise send
   * the old city). Returns false when the field still shows the current city.
   */
  commitTyped: () => boolean;
}

const COPY = {
  en: {
    search: "Search city...",
    field: "Search any town or city",
    none: "No cities found",
    noPlace: "No places found",
    searching: "Searching…",
  },
  hi: {
    search: "शहर खोजें...",
    field: "कोई भी शहर या गाँव खोजें",
    none: "कोई शहर नहीं मिला",
    noPlace: "कोई स्थान नहीं मिला",
    searching: "खोज रहे हैं…",
  },
} as const;

const SEARCH_DEBOUNCE_MS = 300;
/** Below Tailwind's `sm`: the phone layout with the floating bottom chrome. */
const PHONE_MAX_WIDTH = 640;

interface PlaceSearchOptions {
  /** Skip the debounce: the seeker already asked (Enter / Show). */
  immediate?: boolean;
  /** Runs with each answer as it lands, before the next render. */
  onAnswer?: (query: string, places: PlaceResult[]) => void;
}

/**
 * Debounced place search. Returns null while the answer for `query` is still
 * pending (so "nothing found" never flashes before the search ran), else the
 * places found for it. Queries shorter than two characters are not sent.
 */
function usePlaceResults(
  query: string,
  countryFilter?: string,
  { immediate = false, onAnswer }: PlaceSearchOptions = {}
): PlaceResult[] | null {
  const [answer, setAnswer] = useState<{ query: string; places: PlaceResult[] }>({
    query: "",
    places: [],
  });
  const answered = useEffectEvent((answeredQuery: string, places: PlaceResult[]) => {
    onAnswer?.(answeredQuery, places);
  });
  useEffect(() => {
    if (query.length < 2) return;
    let cancelled = false;
    const timer = setTimeout(
      () => {
        void searchPlaces(query, 5, countryFilter).then((places) => {
          if (cancelled) return;
          startTransition(() => setAnswer({ query, places }));
          answered(query, places);
        });
      },
      immediate ? 0 : SEARCH_DEBOUNCE_MS
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [countryFilter, immediate, query]);
  if (query.length < 2) return [];
  return answer.query === query ? answer.places : null;
}

function placeAsCity(place: PlaceResult): SavedCity {
  return {
    name: place.name,
    latitude: place.latitude,
    longitude: place.longitude,
    timezone: place.timezone,
    country: place.country,
  };
}

/** An option tap must not blur the field first (that would close the list). */
function keepFocus(event: MouseEvent) {
  event.preventDefault();
}

export function TimingCitySelector(props: TimingCitySelectorProps) {
  if (props.variant === "field") return <TimingCitySearchField {...props} />;
  return <TimingCityPopover {...props} />;
}

function TimingCityPopover({
  city,
  onCitySelect,
  countryFilter,
  locale = "en",
  onPlaceSelect,
}: TimingCitySelectorProps) {
  const copy = COPY[locale];
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const searchResults = usePlaceResults(searchQuery, countryFilter);

  function handleSelect(place: PlaceResult) {
    setShowSearch(false);
    if (onPlaceSelect) {
      onPlaceSelect(place);
      return;
    }
    onCitySelect(placeAsCity(place));
    setSearchQuery("");
  }

  return (
    <div className="mt-3 relative inline-block">
      <button
        type="button"
        onClick={() => setShowSearch(!showSearch)}
        className="text-body-small font-medium px-3 py-1.5 rounded-lg transition-colors text-intent-brand border border-ish-border-light bg-black/5"
      >
        {city.name}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="inline-block ml-1.5 -mt-0.5"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {showSearch && (
        <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 w-72 rounded-xl shadow-lg z-20 overflow-hidden bg-ish-surface border border-ish-border-light">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={copy.search}
            autoFocus
            className="w-full px-4 py-3 text-body outline-none bg-transparent text-ish-dark border-b border-ish-border-light"
          />
          {searchResults && searchResults.length > 0 && (
            <div className="max-h-48 overflow-y-auto">
              {searchResults.map((place) => (
                <button
                  type="button"
                  key={place.geoname_id}
                  onClick={() => handleSelect(place)}
                  className="w-full text-left px-4 py-2.5 text-body-small hover:bg-surface-warm transition-colors text-ish-dark"
                >
                  {place.display}
                </button>
              ))}
            </div>
          )}
          {searchQuery.length >= 2 && searchResults?.length === 0 && (
            <p className="px-4 py-3 text-body-small text-ish-text-faint">{copy.none}</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The timing hubs' city control once JavaScript runs: one combobox prefilled
 * with the current city (Drik Panchang's pattern). Typing any town searches the
 * unbounded places API; Arrow keys move, Enter picks, Escape puts the current
 * city back. The list overlays the page, so nothing below it moves.
 *
 * A typed town is never silently swapped for the old city: Enter (or the phone
 * keyboard's Search key) before the results land, or Show after the field lost
 * focus, searches the typed text at once and opens the first match. On a phone
 * the field scrolls to the top of the viewport when focused, so the list opens
 * above the keyboard and clear of the floating bottom chrome.
 */
function TimingCitySearchField({
  city,
  onCitySelect,
  countryFilter,
  locale = "en",
  onPlaceSelect,
  fieldClassName,
  fieldRef,
}: TimingCitySelectorProps) {
  const copy = COPY[locale];
  const listId = useId();
  const [query, setQuery] = useState(city.name);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  /** The text whose first match opens as soon as its answer lands. */
  const [commitFor, setCommitFor] = useState<string | null>(null);
  /** The place already chosen (the page is navigating to it). */
  const [picked, setPicked] = useState<string | null>(null);
  const typed = query.trim();
  const dirty = typed !== city.name;
  const unpicked = dirty && typed.length >= 2 && typed !== picked;
  const committing = unpicked && commitFor === typed;
  const searching = (open || committing) && unpicked;
  const places = usePlaceResults(searching ? typed : "", countryFilter, {
    immediate: committing,
    onAnswer: (answeredQuery, found) => {
      if (answeredQuery !== commitFor || answeredQuery !== typed) return;
      // Nothing found: the list says so and the old city stays out of the URL.
      if (found.length > 0) choose(found[0]);
    },
  });
  const expanded = searching && places !== null;
  const busy = committing && places === null;

  function close(restore: boolean) {
    setOpen(false);
    setActive(-1);
    setCommitFor(null);
    if (restore) setQuery(city.name);
  }

  function choose(place: PlaceResult) {
    close(false);
    setQuery(place.name);
    setPicked(place.name);
    if (onPlaceSelect) onPlaceSelect(place);
    else onCitySelect(placeAsCity(place));
  }

  /** Resolve the typed text now; a no-op while it is already resolving. */
  function commit() {
    if (!unpicked) return;
    if (expanded && places && places.length > 0) {
      choose(places[Math.max(active, 0)]);
      return;
    }
    if (expanded) return; // answered: "No places found" is already showing
    setOpen(true);
    setCommitFor(typed);
  }

  useImperativeHandle(fieldRef, () => ({
    commitTyped: () => {
      if (!dirty) return false;
      commit();
      return true;
    },
  }));

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      close(true);
      return;
    }
    if (event.key === "Enter") {
      // An untouched field submits the current city (the Date + Show path).
      if (!dirty) return;
      event.preventDefault();
      commit();
      return;
    }
    if (!expanded || !places?.length) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) => (current + step + places.length) % places.length);
    }
  }

  return (
    <div className="relative">
      <input
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-activedescendant={expanded && active >= 0 ? `${listId}-${active}` : undefined}
        aria-busy={busy || undefined}
        autoComplete="off"
        spellCheck={false}
        enterKeyHint="search"
        value={query}
        placeholder={copy.field}
        onFocus={(event) => {
          const field = event.currentTarget;
          field.select();
          // The field sits low in a phone's first screen; brought to the top
          // (its caption clear of the 100px sticky header via scroll-mt-32),
          // the list has room above the keyboard and the floating chrome.
          if (window.innerWidth < PHONE_MAX_WIDTH) {
            field.scrollIntoView({ block: "start", behavior: "auto" });
          }
          if (unpicked) setOpen(true);
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Keep a typed town (Show resolves it); put the city back over
          // anything too short to search. A search still in flight finishes.
          setOpen(false);
          setActive(-1);
          if (!busy) setCommitFor(null);
          if (typed.length < 2) setQuery(city.name);
        }}
        className={`${fieldClassName ?? ""} scroll-mt-32`}
      />
      <CitySearchList
        listId={listId}
        places={places}
        active={active}
        expanded={expanded}
        busy={busy}
        copy={copy}
        onChoose={choose}
      />
    </div>
  );
}

/** The combobox's overlay: matches, a "searching" line, or "nothing found". */
function CitySearchList({
  listId,
  places,
  active,
  expanded,
  busy,
  copy,
  onChoose,
}: {
  listId: string;
  places: PlaceResult[] | null;
  active: number;
  expanded: boolean;
  busy: boolean;
  copy: (typeof COPY)[keyof typeof COPY];
  onChoose: (place: PlaceResult) => void;
}) {
  if (!expanded && !busy) return null;
  const found = places ?? [];
  return (
    <div className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-xl border border-border-default bg-surface-raised shadow-lg">
      {/* Options are list items driven from the combobox (keyboard focus
          stays in the field), not buttons inside a GET form. */}
      <ul id={listId} role="listbox" className="max-h-60 overflow-y-auto">
        {found.map((place, index) => (
          <li
            key={place.geoname_id}
            id={`${listId}-${index}`}
            role="option"
            aria-selected={index === active}
            onMouseDown={keepFocus}
            onClick={() => onChoose(place)}
            className={`flex min-h-11 cursor-pointer items-center px-4 py-2 text-body-small font-normal text-ish-dark hover:bg-surface-warm ${
              index === active ? "bg-surface-warm" : ""
            }`}
          >
            {place.display}
          </li>
        ))}
      </ul>
      {busy ? (
        <p role="status" className="px-4 py-3 text-body-small font-normal text-ish-text-faint">
          {copy.searching}
        </p>
      ) : null}
      {expanded && found.length === 0 ? (
        <p className="px-4 py-3 text-body-small font-normal text-ish-text-faint">{copy.noPlace}</p>
      ) : null}
    </div>
  );
}

// Compatibility export: the selector began inside Choghadiya, but timing tools
// now share it. Existing imports keep working while new surfaces use the
// capability-level name.
export const ChoghadiyaCitySelector = TimingCitySelector;
